import { ReactNode, useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { useHasScope } from "../../../utils/Permission";
import Download from "../../../assets/Download";
import ForceSyncAllButton from "./ForceSyncAllButton";
import ForceSyncSelectedButton, { EntityLabel } from "./ForceSyncSelectedButton";

const ERROR_STATUS = "Error";
const RECORDS: EntityLabel = { one: "record", many: "records" };

type Props =
    {
        setSearch?: (value: string) => void
        search?: string
        setStatus?: (value: string) => void
        status?: string
        searchExtras?: ReactNode
        onClear?: () => void
        downloadCSV?: () => void
        isDownloading?: boolean
        onRefresh?: () => void
        onForceSyncAll?: () => Promise<string>
        onForceSyncSelected?: () => Promise<string>
        selectedCount?: number
        entityLabel?: EntityLabel
        onClearSelection?: () => void
        isSyncing?: boolean
        isLoading?: boolean
    }

export default function StoreReportToolbar({
    setSearch, search, setStatus, status, searchExtras, onClear, downloadCSV, isDownloading, onRefresh,
    onForceSyncAll, onForceSyncSelected, selectedCount = 0, entityLabel = RECORDS, onClearSelection, isSyncing,
    isLoading,
}: Props) {
    const canForceSyncAll = useHasScope('write-force-sync');
    const showSelection = onClearSelection !== undefined && (selectedCount > 0 || !canForceSyncAll);

    const [term, setTerm] = useState<string>(search ?? "");

    const isFiltered = Boolean(search) || Boolean(status);

    const canClear = term !== "" || isFiltered;

    useEffect(() => {
        setTerm(search ?? "");
    }, [search]);

    const handleSearch = () => setSearch?.(term.trim());

    const handleClear = () => {
        setTerm("");
        setStatus?.("");

        if (onClear) onClear();
        else setSearch?.("");
    };

    const classes = [
        "filterBar productFilters report-toolbar",
        selectedCount > 0 ? "has-selection" : "",
        isLoading ? "store-report-filter-bar-disabled" : "",
    ].filter(Boolean).join(" ");

    return (
        <div className={classes}>
            <div className="toolbar-left">
                <div className="search-field">
                    <input
                        type="text"
                        placeholder="Search.."
                        id="product-search"
                        value={term}
                        readOnly={isLoading}
                        onChange={(e) => setTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                    />
                    <button type="button" id="search-button" onClick={handleSearch} disabled={isLoading}>
                        <img src="https://cdn.shopify.com/s/files/1/0622/8577/5024/files/descarga.png?v=1699470094" alt="" />
                    </button>
                </div>
                {searchExtras}
                {setStatus && (
                    <button
                        type="button"
                        id="error-filter"
                        className={`report-error-filter ${status === ERROR_STATUS ? 'is-active' : ''}`}
                        aria-pressed={status === ERROR_STATUS}
                        disabled={isLoading}
                        onClick={() => setStatus(status === ERROR_STATUS ? "" : ERROR_STATUS)}
                    >
                        Errors only
                        <span className="report-error-switch" aria-hidden="true"></span>
                    </button>
                )}
                <button type="button" className="filter-button cBtn" onClick={handleClear} disabled={isLoading || !canClear}>
                    Clear
                </button>
            </div>
            <div className="toolbar-right">
                {isLoading ? <>
                    {onRefresh && <Skeleton animation={false} variant="rounded" className="toolbar-skeleton" width={67} height={38} />}
                    {downloadCSV && <Skeleton animation={false} variant="rounded" className="toolbar-skeleton" width={67} height={38} />}
                    <Skeleton animation={false} variant="rounded" className="toolbar-skeleton sync" width={190} height={43} />
                </> : <>
                {onRefresh && (
                    <button
                        type="button"
                        className="filter-button cBtn"
                        onClick={onRefresh}
                        title="Refresh the report"
                        aria-label="Refresh the report"
                    >
                        <Refresh fontSize="small" sx={{ color: '#5493DA' }} />
                    </button>
                )}
                {downloadCSV && (
                    <button
                        type="button"
                        className="filter-button cBtn"
                        onClick={downloadCSV}
                        disabled={isDownloading}
                        title={isDownloading
                            ? "Preparing the file…"
                            : isFiltered ? "Download the filtered report as CSV" : "Download the whole report as CSV"}
                    >
                        <Download />
                    </button>
                )}
                {showSelection && onClearSelection
                    ? <ForceSyncSelectedButton
                        selectedCount={selectedCount}
                        entityLabel={entityLabel}
                        onForceSyncSelected={onForceSyncSelected}
                        onClearSelection={onClearSelection}
                        isSyncing={isSyncing}
                      />
                    : <ForceSyncAllButton onForceSyncAll={onForceSyncAll} isSyncing={isSyncing} />}
                </>}
            </div>
        </div>
    )
}
